import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/impact", label: "Impact" },
  { to: "/collaborate", label: "Collaborate" },
  { to: "/journal", label: "Journal" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-ecospora-cream/95 backdrop-blur-md border-b border-ecospora-green-deep/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="font-display text-2xl font-bold tracking-tight text-ecospora-green-deep">
            Ecospora
          </span>
          <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-ecospora-ochre group-hover:scale-150 transition-transform" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-ecospora-green-deep"
                    : "text-ecospora-charcoal/70 hover:text-ecospora-green-deep"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1 text-xs font-medium" data-testid="lang-toggle">
            <button
              onClick={() => setLang("EN")}
              data-testid="lang-en"
              className={`px-2 py-1 rounded ${
                lang === "EN" ? "text-ecospora-green-deep" : "text-ecospora-charcoal/40"
              }`}
            >
              EN
            </button>
            <span className="text-ecospora-charcoal/30">/</span>
            <button
              onClick={() => setLang("BM")}
              data-testid="lang-bm"
              className={`px-2 py-1 rounded ${
                lang === "BM" ? "text-ecospora-green-deep" : "text-ecospora-charcoal/40"
              }`}
            >
              BM
            </button>
          </div>

          <Link
            to="/contact"
            className="hidden md:inline-flex btn-primary !px-5 !py-3 !text-xs"
            data-testid="nav-cta-talk"
          >
            Contact
          </Link>

          <button
            className="lg:hidden text-ecospora-green-deep p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-ecospora-cream border-t border-ecospora-green-deep/10" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="font-display text-2xl text-ecospora-green-deep"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary mt-4 self-start" data-testid="mobile-nav-cta">
              Contact
            </Link>
            <div className="flex gap-2 text-xs pt-2 border-t border-ecospora-green-deep/10">
              <button onClick={() => setLang("EN")} className={lang === "EN" ? "text-ecospora-green-deep font-semibold" : ""}>
                EN
              </button>
              <span>/</span>
              <button onClick={() => setLang("BM")} className={lang === "BM" ? "text-ecospora-green-deep font-semibold" : ""}>
                BM
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
